<script lang="js" setup>
import { ref, onMounted } from "vue";
import { fetchGetApi } from "@/util/api";
import jsonToExcel from '@/util/makeExcel';
import Swal from 'sweetalert2';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';
import { defineAsyncComponent } from 'vue';

const modalCode = defineAsyncComponent(() => import("@/components/roles/modalCode.vue"));
const elementsPerPage = ref(25);
const currentPage = ref(1);
const filterQuery = ref("");
const toast = useToast();
const allData = ref([]);
const startDate = ref("");
const endDate = ref("");
const sites = ref(["Site 11"]);
const selectedSite = ref([]);

onMounted(async () => {
    getData();
});

function getData() {
    fetchGetApi('api/operation/get/report/basicHousekeeping').then((response) => {
        if (response?.data?.data) {
            if (response.data.data.length > 0) {
                allData.value = response.data.data;
                console.log(allData.value);
            } else {
                toast.warning('No data available');
            }
        } else {
            toast.error('Invalid response format');
        }
    }).catch((error) => {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch data');
    });
}

function downloadExcel() {
    const exportData = allData.value.flatMap(site => 
        site.records.map(record => ({
            'Site Name': site.siteName || 'N/A',
            'Site Address': site.siteAddress || 'N/A',
            'Area': site.area || 'N/A',
            'Problem': record.problem || 'N/A',
            'Solution': record.solution || 'N/A',
            'Category': record.category || 'N/A',
            'Status': record.status || 'N/A',
            'Total Employees': record.statistics?.totalEmployees || 0,
            'Employees with Penalty': record.statistics?.employeesWithPenalty || 0,
            'Total Penalty Amount': record.statistics?.totalPenaltyAmount || 0,
            'Employees with Documentation': record.statistics?.employeesWithDocumentation || 0,
            'Employees with Action': record.statistics?.employeesWithAction || 0,
            'Sub Categories Count': record.statistics?.subCategoriesCount || 0,
            'Safety Rating': `${record.statistics?.safetyRating?.toFixed(2) || 0}%`,
            'Compliance Rate': `${record.statistics?.complianceRate || 0}%`,
            'Documentation Rate': `${record.statistics?.documentationRate || 0}%`,
            'Action Compliance Rate': `${record.statistics?.actionComplianceRate || 0}%`,
            'Photo Compliance Rate': `${record.statistics?.photoComplianceRate || 0}%`,
            'Slip Compliance Rate': `${record.statistics?.slipComplianceRate || 0}%`
        }))
    );
    
    jsonToExcel(exportData, 'basic-housekeeping-report.xlsx');
}

function search() {
    // Implement the search functionality based on date range and selected sites
    toast.info('Search functionality will be implemented');
}

function showSubCategories(subCategories) {
    const html = `
        <table class="table table-bordered">
            <thead>
                <tr>
                    <th>Sub Category</th>
                    <th>Problem</th>
                    <th>Solution</th>
                </tr>
            </thead>
            <tbody>
                ${subCategories.map(cat => `
                    <tr>
                        <td>${cat.subCategory || 'N/A'}</td>
                        <td>${cat.problem || 'N/A'}</td>
                        <td>${cat.solution || 'N/A'}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    
    Swal.fire({
        title: 'Sub Categories',
        html: html,
        width: '80%',
        showCloseButton: true,
        showConfirmButton: false
    });
}

function showEmployeeDetails(employees) {
    const html = `
        <table class="table table-bordered">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Action</th>
                    <th>Penalty Amount</th>
                    <th>Action Date</th>
                    <th>Action Time</th>
                    <th>Photo</th>
                    <th>Slip</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                ${employees.map(emp => `
                    <tr>
                        <td>${emp.name || 'N/A'}</td>
                        <td>${emp.action || 'N/A'}</td>
                        <td>${emp.penaltyAmount || 0}</td>
                        <td>${emp.actionDate || 'N/A'}</td>
                        <td>${emp.actionTime || 'N/A'}</td>
                        <td>
                            ${emp.uploadPhoto ? 
                                `<a href="${emp.uploadPhoto}" target="_blank" class="btn btn-sm btn-outline-primary">
                                    <i class="fas fa-eye me-1"></i>View Photo
                                </a>` : 
                                'N/A'}
                        </td>
                        <td>
                            ${emp.slip ? 
                                `<a href="${emp.slip}" target="_blank" class="btn btn-sm btn-outline-primary">
                                    <i class="fas fa-file me-1"></i>View Slip
                                </a>` : 
                                'N/A'}
                        </td>
                        <td>${emp.status || 'N/A'}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    
    Swal.fire({
        title: 'Employee Details',
        html: html,
        width: '90%',
        showCloseButton: true,
        showConfirmButton: false
    });
}

function num_pages() {
    const query = filterQuery.value.toLowerCase();
    const data = Array.isArray(allData.value) ? allData.value : [];
    const filteredData = data.filter(item =>
        item.siteName.toLowerCase().includes(query) ||
        item.records.some(record => 
            record.problem.toLowerCase().includes(query) ||
            (record.solution && record.solution.toLowerCase().includes(query))
        )
    );
    return Math.ceil(filteredData.length / elementsPerPage.value);
}

function change_page(page) {
    currentPage.value = page;
}

function previousPage() {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
}

function nextPage() {
    if (currentPage.value < num_pages()) {
        currentPage.value++;
    }
}
</script>

<template>
    <Breadcrumbs main="Apps" title="Basic Housekeeping Report" />
    <div class="container-fluid">
        <div class="email-wrap bookmark-wrap">
            <div class="row card card-body">
                <div class="table-responsive product-table custom-scrollbar">
                    <form class="no-footer" @submit.prevent>
                        <div class="mb-3 row justify-content-between">
                            <div class="col-xs-12 col-md-4">
                                <label class="col-form-label">Date range:</label>
                                <div class="d-flex align-items-center">
                                    <input type="date" class="form-control" v-model="startDate">
                                    <span class="mx-1">to</span>
                                    <input type="date" class="form-control" v-model="endDate">
                                </div>
                            </div>
                            <div class="col-xs-12 col-md-4">
                                <label for="area-select" class="col-form-label">Search by area:</label>
                                <multiselect id="area-select" v-model="selectedSite" :options="sites" :multiple="true"
                                    :searchable="true" placeholder="Select areas" label="name" track-by="name"
                                    class="form-control"></multiselect>
                            </div>
                            <div class="col-xs-12 col-md-4 d-flex align-items-end">
                                <div class="d-flex gap-2">
                                    <button type="button" class="btn btn-primary" @click.prevent="search">
                                        <i class="fas fa-search me-1"></i> Search
                                    </button>
                                    <button type="button" class="btn btn-success" @click.prevent="downloadExcel">
                                        <i class="fas fa-file-excel me-1"></i> Download Excel
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="table-responsive">
                            <table class="display table-striped table-hover table-bordered table" id="basic-1">
                                <thead>
                                    <tr>
                                        <th class="sorting_asc" scope="col" style="width: 50px;">SL</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Site Name</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Site Address</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Area</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Problem</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Solution</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Category</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Sub Categories</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Employee Details</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Status</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Safety Rating</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="(site, siteIndex) in allData" :key="site.siteId">
                                        <tr v-for="(record, recordIndex) in site.records" :key="recordIndex">
                                            <td>{{ (siteIndex * site.records.length) + recordIndex + 1 }}</td>
                                            <td>{{ site.siteName || 'N/A' }}</td>
                                            <td>{{ site.siteAddress || 'N/A' }}</td>
                                            <td>{{ site.area || 'N/A' }}</td>
                                            <td>{{ record.problem || 'N/A' }}</td>
                                            <td>{{ record.solution || 'N/A' }}</td>
                                            <td>{{ record.category || 'N/A' }}</td>
                                            <td>
                                                <button type="button" class="btn btn-sm btn-info" @click.stop="showSubCategories(record.subCategories)">
                                                    <i class="fas fa-list me-1"></i>View Sub Categories
                                                </button>
                                            </td>
                                            <td>
                                                <button type="button" class="btn btn-sm btn-primary" @click.stop="showEmployeeDetails(record.employees)">
                                                    <i class="fas fa-users me-1"></i>View Employees
                                                </button>
                                            </td>
                                            <td>{{ record.status || 'N/A' }}</td>
                                            <td>{{ record.statistics?.safetyRating?.toFixed(2) || 0 }}%</td>
                                        </tr>
                                    </template>
                                    <tr v-if="!allData.length">
                                        <td colspan="11" class="text-center py-3">No data available</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="d-flex justify-content-between align-items-center mt-3">
                            <div>
                                <select v-model="elementsPerPage" class="form-select form-select-sm">
                                    <option :value="10">10 per page</option>
                                    <option :value="25">25 per page</option>
                                    <option :value="50">50 per page</option>
                                    <option :value="100">100 per page</option>
                                </select>
                            </div>
                            <ul class="pagination py-2 justify-content-end pagination-primary mb-0">
                                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                    <a class="page-link" @click="previousPage">Previous</a>
                                </li>
                                <li class="page-item" v-for="i in num_pages()" :key="i"
                                    :class="[i == currentPage ? 'active' : '']" @click="change_page(i)">
                                    <a class="page-link">{{ i }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: currentPage >= num_pages() }">
                                    <a class="page-link" @click="nextPage">Next</a>
                                </li>
                            </ul>
                        </div>
                    </form>
                    <modalCode />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.btn {
    white-space: nowrap;
}

@media (max-width: 767px) {
    .table-responsive {
        overflow-x: auto;
    }
    
    .table th,
    .table td {
        white-space: nowrap;
        min-width: 100px;
    }
    
    .d-flex.align-items-center {
        flex-direction: column;
        align-items: stretch !important;
    }
    
    .d-flex.align-items-center .mx-1 {
        margin: 0.5rem 0;
        text-align: center;
    }
}
</style>
